import { Link } from "react-router-dom";
import { Modal } from "./Modal";
import type { ExistingItemRef } from "../api/types";

interface Props {
  /** The name about to be created a second time. */
  name: string;
  /** The active items that already carry it (from the server's 409). */
  existing: ExistingItemRef[];
  /** What confirming does: "Create it anyway", "Promote it anyway". */
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * G-119 — the soft guard's question. The server refused a second active
 * item with this name; the person either picks the existing one (the links
 * open it in a new tab so the form they were filling survives) or says the
 * second one is wanted — two suppliers, two variants — and the create runs
 * again with `confirmDuplicateOf`, the ids of exactly these twins.
 */
export function DuplicateNameDialog({
  name,
  existing,
  confirmLabel,
  busy = false,
  onConfirm,
  onClose,
}: Props) {
  return (
    <Modal title="An item with this name already exists" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p style={{ margin: 0 }}>
          {existing.length === 1 ? "There is already an item called" : `There are already ${existing.length} items called`}{" "}
          <strong>{existing[0]?.name ?? name}</strong>. Creating <strong>{name}</strong> makes{" "}
          {existing.length === 1 ? "a second one" : "another one"} — fine for the same flower from
          two suppliers, a mistake if it is the same product.
        </p>
        <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
          {existing.map((item) => (
            <li key={item.id}>
              <Link to={`/app/items/${item.id}`} target="_blank" rel="noreferrer">
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
        <div className="button-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
